import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import { setIsWidgetReady } from 'redux/app/app.actions';
import { Dashboard, Auth } from './layouts';
import { WithAuth, WithAdminAuth, WithPublicAuth } from './hoc';
import routes from './routes';

import { Helmet } from 'react-helmet';
// import 'react-loader-spinner/dist/loader/css/react-spinner-loader.css';
import { LineWave } from 'react-loader-spinner';
import 'animate.css';
import 'react-loading-skeleton/dist/skeleton.css';
import useTranslation from 'hooks/useTranslation';
import { ToastContainer } from 'react-toastify';
import {
  RAYCHAT_WIDGET_TOKEN,
  RAYCHAT_WIDGET_INSTALLED_ON_DASHBOARD_URL,
} from 'utils/constants';
import UserDetailsConvArchive from 'pages/ConversationsArchivePage/UserDetailsConvArchive';
// import { fetchWebsitesStart } from 'redux/websites/websites.actions';
// import { useAuth } from 'hooks';

const LoginCard = React.lazy(() => import('./components/Auth/LoginCard'));
const OneTimeVerification = React.lazy(() =>
  import('./components/Auth/OneTimeVerification')
);

const WebsiteListPage = React.lazy(() => import('./pages/WebsiteListPage'));
const OperatorsPage = React.lazy(() => import('./pages/OperatorsPage'));
const ConversationsPage = React.lazy(() => import('./pages/ConversationsPage'));
const BlackListPage = React.lazy(() => import('./pages/BlackListPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const PaymentPage = React.lazy(() => import('./pages/PaymentPage'));
const ConnectWhatsapp = React.lazy(() => import('./pages/ConnectWhatsapp'));
// const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
// const LiveUsersPage = React.lazy(() => import('./pages/LiveUsersPage'));
const PaymentReportPage = React.lazy(() => import('./pages/PaymentReportPage'));
const SettingsTeam = React.lazy(() => import('./pages/SettingsTeam'));
const GroupMessage = React.lazy(() => import('./pages/GroupMessage'));
const ConvsArchivePage = React.lazy(() =>
  import('./pages/ConversationsArchivePage')
);
const WidgetInstallation = React.lazy(() =>
  import('./pages/WidgetInstallation')
);
const Teams = React.lazy(() => import('./pages/Teams'));
const WebhookPage = React.lazy(() => import('./pages/WebhookPage'));
// const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const AppsList = React.lazy(() => import('pages/AppsList'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const AffiliatePage = React.lazy(() => import('./pages/AffiliatePage'));
const KnowledgeBasePage = React.lazy(() => import('./pages/KnowledgeBasePage'));

const mapState = ({ appData }) => ({
  locale: appData.locale,
});

const TOKEN_BASED_ON_LOCALE = {
  en: RAYCHAT_WIDGET_TOKEN,
  fa: RAYCHAT_WIDGET_TOKEN,
};

function App() {
  const dispatch = useDispatch();
  const { locale } = useSelector(mapState);
  // const isAuthenticated = useAuth();

  const { getTranslation } = useTranslation('COMMON');
  const { t } = getTranslation();

  /**
    to prevent flashy position transition of widget, it is set into `display none` in `index.scss` file
    by this function the widget will be set visible again when custom position is set  
   */
  const makeWidgetVisibleAfterPositionChange = () => {
    const widgetElement = document.getElementById('raychat_widget');
    if (widgetElement) {
      widgetElement.style.display = 'block';
    }
  };

  // set listener when widget is loaded
  useEffect(() => {
    const callBack = () => {
      // set custom position
      window.Raychat.setPosition({
        top: 'auto',
        right: 'auto',
        bottom: '15px',
        left: '15px',
      });

      // short timeout just to prevent flash effect
      setTimeout(makeWidgetVisibleAfterPositionChange, 100);

      dispatch(setIsWidgetReady(true));
    };

    window.addEventListener('raychat_ready', callBack);

    return () => window.removeEventListener('raychat_ready', callBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // widget script
  useEffect(() => {
    window.RAYCHAT_TOKEN = TOKEN_BASED_ON_LOCALE[locale];
    let d = document;
    let s = d.createElement('script');
    s.src = RAYCHAT_WIDGET_INSTALLED_ON_DASHBOARD_URL;
    s.async = 1;
    d.getElementsByTagName('head')[0].appendChild(s);
  }, []);

  useEffect(() => {
    if (locale === 'fa') {
      document.querySelector('body').setAttribute('dir', 'rtl');
      document.querySelector('html').setAttribute('lang', 'fa');
      // localStorage.setItem('raychat_dashboard_locale', 'fa');
    } else {
      document.querySelector('body').setAttribute('dir', 'ltr');
      document.querySelector('html').setAttribute('lang', 'en');
      // localStorage.setItem('raychat_dashboard_locale', 'en');
    }
  }, [locale]);

  // useEffect(() => {
  //   if (isAuthenticated) {
  //     dispatch(fetchWebsitesStart());
  //   }
  // }, [isAuthenticated]);

  return (
    <>
      <ToastContainer
        position='bottom-left'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={locale === 'fa'}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Helmet>
        <title>{t('DOCUMENT_PAGE_TITLE')}</title>
      </Helmet>
      <Switch>
        <Route exact path='/'>
          <Redirect to={routes.websites} />
        </Route>
        <Route
          exact
          path={routes.websites}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <WebsiteListPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          path={routes.login}
          render={() => (
            <WithPublicAuth>
              <Auth pageName='login' hasLogos>
                <Suspense
                  fallback={
                    <div className='loader_container'>
                      <LineWave
                        height={120}
                        width={120}
                        color='rgb(177, 14, 132)'
                        ariaLabel='line-wave'
                        wrapperStyle={{}}
                        wrapperClass=''
                        visible={true}
                      />
                    </div>
                  }
                >
                  <LoginCard />
                </Suspense>
              </Auth>
            </WithPublicAuth>
          )}
        />
        <Route
          path={routes.signup}
          render={() => (
            <WithPublicAuth>
              <Auth pageName='signup' hasLogos>
                <Suspense
                  fallback={
                    <div className='loader_container'>
                      <LineWave
                        height={120}
                        width={120}
                        color='rgb(177, 14, 132)'
                        ariaLabel='line-wave'
                        wrapperStyle={{}}
                        wrapperClass=''
                        visible={true}
                      />
                    </div>
                  }
                >
                  <SignupPage />
                </Suspense>
              </Auth>
            </WithPublicAuth>
          )}
        />
        <Route
          path={routes.oneTimeVerification}
          render={() => (
            <WithPublicAuth>
              <Auth pageName='two-step-verification' hasLogos>
                <Suspense
                  fallback={
                    <div className='loader_container'>
                      <LineWave
                        height={120}
                        width={120}
                        color='rgb(177, 14, 132)'
                        ariaLabel='line-wave'
                        wrapperStyle={{}}
                        wrapperClass=''
                        visible={true}
                      />
                    </div>
                  }
                >
                  <OneTimeVerification />
                </Suspense>
              </Auth>
            </WithPublicAuth>
          )}
        />

        <Route
          path={routes.operators}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <OperatorsPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          path={routes.conversations}
          render={() => (
            <WithAuth>
              <Dashboard>
                <ConversationsPage />
              </Dashboard>
            </WithAuth>
          )}
        />
        <Route
          path={routes.blacklist}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <BlackListPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.reports}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <ReportsPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          path={routes.reportDetails}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <ReportsPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.gatewayReport}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <PaymentReportPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.paymentPage}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <PaymentPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.connectWhatsapp}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <ConnectWhatsapp />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.settingsTeam}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <SettingsTeam />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.groupMessage}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <GroupMessage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          path={routes.webhook}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <WebhookPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.widgetInstallation}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <WidgetInstallation />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
        <Route
          exact
          path={routes.teams}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <Teams />
              </Dashboard>
            </WithAdminAuth>
          )}
        />

        <Route
          path={routes.faq}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <FAQPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />

        <Route
          path={routes.appsList}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <AppsList />
              </Dashboard>
            </WithAdminAuth>
          )}
        />

        <Route
          path={routes.knowledgeBasePage}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <KnowledgeBasePage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />

        <Route
          path={routes.conversationsArchiveList}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <ConvsArchivePage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />

        <Route
          path={routes.userDetailsConvArchive}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <UserDetailsConvArchive />
              </Dashboard>
            </WithAdminAuth>
          )}
        />

        <Route
          path={routes.affiliatePage}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <AffiliatePage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />

        {/* <Route
        exact
        path='/payment/:paymentPage/:billId'
        render={() => (
          <WithAdminAuth>
            <Dashboard>
              <PaymentPage />
            </Dashboard>
          </WithAdminAuth>
        )}
      />
      <Route
        path='/notifications'
        render={() => (
          <WithAdminAuth>
            <Dashboard>
              <NotificationsPage />
            </Dashboard>
          </WithAdminAuth>
        )}
      />
      <Route
        path='/live-users'
        render={() => (
          <WithAuth>
            <Dashboard>
              <LiveUsersPage />
            </Dashboard>
          </WithAuth>
        )}
      /> */}

        {/* <Route
        exact
        path={routes.profile}
        render={() => (
          <WithAdminAuth>
            <Dashboard>
              <ProfilePage />
            </Dashboard>
          </WithAdminAuth>
        )}
      /> */}

        <Route
          exact
          path={routes.settingsWidget}
          render={() => (
            <WithAdminAuth>
              <Dashboard>
                <SettingsPage />
              </Dashboard>
            </WithAdminAuth>
          )}
        />
      </Switch>
    </>
  );
}

export default App;
